/** Detección de transientes (aplausos, chasquidos) vía energía / pico RMS. */

export interface OnsetSample {
  rms: number;
  peakRms: number;
  volume: number;
}

export interface OnsetDetectorConfig {
  /** Pico mínimo absoluto para considerar un ataque. */
  minPeakRms: number;
  /** Subida relativa respecto al frame anterior (ej. 1.45 = +45%). */
  riseRatio: number;
  /** Multiplicador sobre la línea de ruido adaptativa. */
  noiseMultiplier: number;
  /** Tiempo mínimo entre onsets (evita dobles disparos). */
  cooldownMs: number;
}

export const DEFAULT_ONSET_CONFIG: OnsetDetectorConfig = {
  minPeakRms: 0.00032,
  riseRatio: 1.42,
  noiseMultiplier: 2.15,
  cooldownMs: 110,
};

export interface OnsetDetectorState {
  prevPeakRms: number;
  noiseBaseline: number;
  lastOnsetAt: number;
}

export function createOnsetDetectorState(): OnsetDetectorState {
  return { prevPeakRms: 0, noiseBaseline: 0, lastOnsetAt: 0 };
}

export function updateOnsetNoiseBaseline(
  baseline: number,
  rms: number,
  volume: number,
  quietThreshold = 0.0012
): number {
  if (volume >= quietThreshold) return baseline;
  if (baseline <= 0) return rms;
  return baseline * 0.93 + rms * 0.07;
}

/**
 * ¿Hay un nuevo ataque en este frame?
 * Combina umbral adaptativo + subida brusca de energía en la cola del buffer.
 */
export function detectOnset(
  sample: OnsetSample,
  state: OnsetDetectorState,
  nowMs: number,
  config: OnsetDetectorConfig = DEFAULT_ONSET_CONFIG
): { fired: boolean; next: OnsetDetectorState } {
  const { peakRms, rms, volume } = sample;
  const noiseBaseline = updateOnsetNoiseBaseline(state.noiseBaseline, rms, volume);
  const adaptiveFloor = Math.max(config.minPeakRms, noiseBaseline * config.noiseMultiplier);

  const cooledDown = nowMs - state.lastOnsetAt >= config.cooldownMs;
  const aboveFloor = peakRms >= adaptiveFloor;
  const sharpRise =
    state.prevPeakRms > 0 &&
    peakRms >= state.prevPeakRms * config.riseRatio &&
    peakRms >= config.minPeakRms * 0.85;

  const fired = cooledDown && aboveFloor && (sharpRise || peakRms >= adaptiveFloor * 1.35);

  return {
    fired,
    next: {
      prevPeakRms: peakRms,
      noiseBaseline,
      lastOnsetAt: fired ? nowMs : state.lastOnsetAt,
    },
  };
}

/** Escala sensibilidad 0–100 → config (mayor = más sensible). */
export function onsetConfigFromSensitivity(sensitivity: number): OnsetDetectorConfig {
  const t = Math.min(100, Math.max(0, sensitivity)) / 100;
  return {
    minPeakRms: 0.00055 - t * 0.00028,
    riseRatio: 1.85 - t * 0.55,
    noiseMultiplier: 3.1 - t * 1.2,
    cooldownMs: Math.round(145 - t * 45),
  };
}
